import AppContext from 'App/AppContext';
import { getProjectRoute } from 'components/Project/projectRoutes';
import { useActiveProjects } from 'components/Project/useActiveProjects';
import { ITaskViewStateContextValue, useTaskViewState } from 'components/Tasks/TaskViews/TaskViewStateContext';
import { TFunction } from 'i18next';
import { ProjectSummary } from 'models/projects/project';
import { useCallback, useContext, useLayoutEffect, useMemo, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import {
  ensureExpandStateLoaded,
  getCollapsedIds,
  navGroupStoreId,
  openGroupForVisit,
  subscribeExpandState,
  toggleExpanded as toggleExpandedInStore,
} from './navExpandStore';
import { useNavGroupCaptions, useNavItems } from './navItems';
import { navLayout } from './navLayout';
import {
  INavItemDefinition,
  INavModel,
  INavModelGroup,
  INavModelItem,
  NavGroupCaptionId,
  NavItemId,
} from './navModel';
import { getVisitedChild, setVisitedChild } from './navVisitStore';

//
// Resolves the catalogue (navItems.ts) and the order (navLayout.ts) into the menu one user sees now:
// the visible entries, the current one, and what is open. It also keeps, for the session, which
// view of an expandable layout entry the user opened last, so a click on the entry returns there.
// Independent of how the menu is drawn.
//

const tasksItemId: NavItemId = 'tasks';
const projectsItemId: NavItemId = 'projects';

// The entries the layout nests other entries under. A click on one opens the child visited last in
// this session. Tasks is a plain id in the layout and keeps the redirect its scene does instead.
const layoutParentIds: ReadonlySet<string> = new Set(
  navLayout.body.reduce<string[]>((ids, group) => {
    group.items.forEach((entry) => {
      if (typeof entry !== 'string') {
        ids.push(entry.id);
      }
    });

    return ids;
  }, []),
);

const toModelItem = (definition: INavItemDefinition): INavModelItem => ({
  id: definition.id,
  label: definition.label,
  iconName: definition.iconName,
  className: definition.className,
  route: definition.route,
  matchSegments: definition.matchSegments,
});

/**
 * The location segments a custom task view owns. Its stored url is /view/<routeId>/<slug>; the route
 * id alone identifies the view, so a renamed view keeps its entry.
 */
const toCustomViewSegments = (viewUrl: string): string[] => {
  const segments = viewUrl.split('/').filter((segment) => segment.length > 0);

  return ['tasks', ...segments.slice(0, 2)];
};

/**
 * The item that owns the current location: the deepest match, so /admin/users picks Users rather
 * than a shorter /admin match. Inside an area the menu knows (the first segment belongs to some
 * entry of the catalogue, visible or not) a location without a match highlights nothing; anywhere
 * else falls back to Start.
 */
const findSelectedId = (
  items: INavModelItem[],
  knownAreas: ReadonlySet<string>,
  locationSegments: string[],
): string => {
  let matchedId: string | undefined = undefined;
  let matchedLength = 0;

  for (const item of items) {
    for (const segments of item.matchSegments ?? []) {
      const isMatch =
        segments.length > matchedLength && segments.every((segment, index) => locationSegments[index] === segment);

      if (isMatch) {
        matchedId = item.id;
        matchedLength = segments.length;
      }
    }
  }

  if (matchedId) {
    return matchedId;
  }

  const area = locationSegments[0];

  return area !== undefined && knownAreas.has(area) ? '' : 'start';
};

/**
 * The children of the Tasks entry: the user's own task views, in the order of their tab bar, minus
 * the ones they hid from the sidebar. Template views show unless hidden; custom views hide unless
 * shown, because they are added to the hidden list when created.
 */
const buildTaskViewChildren = (
  taskViewState: ITaskViewStateContextValue,
  canSeeAllTasks: boolean,
  t: TFunction,
): INavModelItem[] => {
  try {
    if (taskViewState.isLayoutLoading) {
      return [];
    }

    const hiddenSidebar = taskViewState.hiddenSidebarViewKeys;
    const hiddenTemplate = taskViewState.hiddenTemplateViewKeys;
    const viewOrder = taskViewState.viewOrder;

    const templateViews = [
      { key: 'mytasks', label: t('tasks:Pivot.MyTasks'), url: '/tasks/mytasks' },
      { key: 'myrequests', label: t('tasks:Pivot.MyRequests'), url: '/tasks/myrequests' },
      {
        key: 'monitoringfinding',
        label: t('tasks:TaskViews.Templates.MonitoringFindings'),
        url: '/tasks/monitoringfinding',
      },
      { key: 'nonconformity', label: t('tasks:TaskViews.Templates.NonConformities'), url: '/tasks/nonconformity' },
      { key: 'incident', label: t('tasks:TaskViews.Templates.Incidents'), url: '/tasks/incident' },
    ];

    if (canSeeAllTasks) {
      templateViews.push({ key: 'alltasks', label: t('tasks:Pivot.AllTasks'), url: '/tasks/alltasks' });
    }

    const templateLinks = templateViews.reduce<(INavModelItem & { viewKey: string })[]>((links, view) => {
      if (hiddenSidebar.includes(view.key) || hiddenTemplate.includes(view.key)) {
        return links;
      }

      links.push({
        id: `tasks-${view.key}`,
        label: view.label,
        route: view.url,
        matchSegments: [['tasks', view.key]],
        viewKey: view.key,
      });

      return links;
    }, []);

    const customLinks = taskViewState.customViews.reduce<(INavModelItem & { viewKey: string })[]>((links, view) => {
      if (hiddenSidebar.includes(view.key)) {
        return links;
      }

      links.push({
        id: `tasks-${view.key}`,
        label: view.label,
        route: `/tasks${view.url}`,
        matchSegments: [toCustomViewSegments(view.url)],
        viewKey: view.key,
      });

      return links;
    }, []);

    // The user's tab-bar order first; views missing from it keep their template-then-custom order.
    const merged = [...templateLinks, ...customLinks];
    const mergedByViewKey = new Map(merged.map((item) => [item.viewKey, item]));
    const orderedKeys = viewOrder.filter((key) => mergedByViewKey.has(key));
    const orderedKeySet = new Set(orderedKeys);
    const missingKeys = merged.reduce<string[]>((keys, item) => {
      if (!orderedKeySet.has(item.viewKey)) {
        keys.push(item.viewKey);
      }

      return keys;
    }, []);

    return [...orderedKeys, ...missingKeys].reduce<INavModelItem[]>((links, key) => {
      const item = mergedByViewKey.get(key);
      if (item) {
        links.push({
          id: item.id,
          label: item.label,
          route: item.route,
          matchSegments: item.matchSegments,
        });
      }

      return links;
    }, []);
  } catch {
    return [];
  }
};

/**
 * One child per active project, for the Projects entry when there is more than one.
 */
const buildProjectChildren = (activeProjects: ProjectSummary[]): INavModelItem[] => {
  return activeProjects.map((project) => ({
    id: `projects-${project.projectId}`,
    label: project.name,
    route: getProjectRoute(project.projectId),
    matchSegments: [['project', String(project.projectId)]],
  }));
};

export const useNavModel = (): INavModel => {
  const { t } = useTranslation(['translation', 'tasks']);
  const appContext = useContext(AppContext);
  const history = useHistory();
  const { pathname } = useLocation();
  const taskViewState = useTaskViewState();
  const navItems = useNavItems();
  const captions = useNavGroupCaptions();

  ensureExpandStateLoaded(appContext);
  const collapsedIds = useSyncExternalStore(subscribeExpandState, getCollapsedIds);

  const canSeeAllTasks = hasUserFeature(appContext, FeatureTypes.GenericManager);
  const taskChildren = useMemo(
    () => buildTaskViewChildren(taskViewState, canSeeAllTasks, t),
    [canSeeAllTasks, t, taskViewState],
  );

  const { activeProjects } = useActiveProjects();
  const projectChildren = useMemo(
    () => (activeProjects.length > 1 ? buildProjectChildren(activeProjects) : []),
    [activeProjects],
  );

  const locationSegments = useMemo(() => pathname.split('/').filter((segment) => segment.length > 0), [pathname]);

  // Everything the user may open, on screen or not: an entry that is scrolled away, collapsed into
  // an icon or inside a closed group still owns its route.
  const selectableItems = useMemo<INavModelItem[]>(() => {
    const all: INavModelItem[] = [];

    (Object.keys(navItems) as NavItemId[]).forEach((itemId) => {
      const definition = navItems[itemId];
      if (definition.isVisible(appContext)) {
        const item = toModelItem(definition);
        if (itemId === projectsItemId && activeProjects.length === 1) {
          item.label = activeProjects[0].name;
          item.route = getProjectRoute(activeProjects[0].projectId);
        } else if (itemId === projectsItemId && activeProjects.length > 1) {
          item.label = t('translation:NavMain.Projects');
        }
        all.push(item);
      }
    });

    taskChildren.forEach((child) => {
      all.push(child);
    });
    projectChildren.forEach((child) => {
      all.push(child);
    });

    return all;
  }, [activeProjects, appContext, navItems, projectChildren, t, taskChildren]);

  // The first URL segments the whole catalogue knows, so an entry hidden for this user still counts
  // as a known area rather than sending the selection back to Start.
  const knownAreas = useMemo(() => {
    const areas = new Set<string>();
    (Object.values(navItems) as INavItemDefinition[]).forEach((definition) => {
      definition.matchSegments.forEach((segments) => {
        if (segments.length > 0) {
          areas.add(segments[0]);
        }
      });
    });

    return areas;
  }, [navItems]);

  const matchedId = useMemo(
    () => findSelectedId(selectableItems, knownAreas, locationSegments),
    [knownAreas, locationSegments, selectableItems],
  );

  const bodyGroups = useMemo<INavModelGroup[]>(() => {
    const groups: INavModelGroup[] = [];

    navLayout.body.forEach((groupLayout) => {
      const groupItems: INavModelItem[] = [];

      groupLayout.items.forEach((entry) => {
        const itemId = typeof entry === 'string' ? entry : entry.id;
        const nestedIds = typeof entry === 'string' ? [] : entry.children;
        // Every nested entry keeps its own gate.
        const nestedChildren = nestedIds
          .map((childId) => navItems[childId])
          .filter((definition) => definition.isVisible(appContext))
          .map(toModelItem);

        const definition = navItems[itemId];
        if (!definition.isVisible(appContext)) {
          // The nested entries take the place of a parent the user may not see, so nesting never
          // changes who sees an entry.
          groupItems.push(...nestedChildren);

          return;
        }

        const item = toModelItem(definition);
        if (itemId === projectsItemId && activeProjects.length === 1) {
          item.label = activeProjects[0].name;
          item.route = getProjectRoute(activeProjects[0].projectId);
        } else if (itemId === projectsItemId && activeProjects.length > 1) {
          item.label = t('translation:NavMain.Projects');
        }
        // The task views and the project list come from the user's own data rather than from the layout.
        const children =
          itemId === tasksItemId ? taskChildren : itemId === projectsItemId ? projectChildren : nestedChildren;
        if (children.length > 0) {
          item.children = children;
          item.isExpanded = !collapsedIds.has(itemId);
        }

        groupItems.push(item);
      });

      if (groupItems.length > 0) {
        const caption = groupLayout.caption;
        // A group without a caption has no header to click, so it can never be closed.
        const isCollapsible = caption !== undefined && groupLayout.isCollapsible !== false;

        groups.push({
          id: caption,
          caption: caption ? captions[caption] : undefined,
          isCollapsible,
          isExpanded: caption === undefined || !isCollapsible || !collapsedIds.has(navGroupStoreId(caption)),
          items: groupItems,
        });
      }
    });

    return groups;
  }, [activeProjects, appContext, captions, collapsedIds, navItems, projectChildren, t, taskChildren]);

  // The group that holds the page the user is on, when that group can be closed.
  const matchedGroupId = useMemo<NavGroupCaptionId | undefined>(() => {
    const group = bodyGroups.find(
      (candidate) =>
        candidate.isCollapsible &&
        candidate.items.some(
          (item) => item.id === matchedId || (item.children ?? []).some((child) => child.id === matchedId),
        ),
    );

    return group?.id;
  }, [bodyGroups, matchedId]);

  // Arriving in a closed group opens it for this visit. The id alone is the dependency: while the
  // user stays in the same group the effect does not run again, so they can close it and keep
  // working on the page they are on.
  useLayoutEffect(() => {
    openGroupForVisit(matchedGroupId ? navGroupStoreId(matchedGroupId) : undefined);
  }, [matchedGroupId]);

  const footerItems = useMemo<INavModelItem[]>(() => {
    const footer: INavModelItem[] = [];

    navLayout.footer.forEach((groupLayout) => {
      const header = navItems[groupLayout.header];
      if (!header.isVisible(appContext)) {
        return;
      }

      const children: INavModelItem[] = [];
      groupLayout.items.forEach((entry) => {
        const itemId = typeof entry === 'string' ? entry : entry.id;
        const nestedChildren = (typeof entry === 'string' ? [] : entry.children)
          .map((childId) => navItems[childId])
          .filter((definition) => definition.isVisible(appContext))
          .map(toModelItem);

        const definition = navItems[itemId];
        if (!definition.isVisible(appContext)) {
          // As in the body: the nested pages take the place of a parent the user may not see.
          children.push(...nestedChildren);

          return;
        }

        const item = toModelItem(definition);
        if (typeof entry !== 'string') {
          // A parent in a pinned group only groups pages; without any it has nothing to show.
          if (nestedChildren.length === 0) {
            return;
          }

          item.children = nestedChildren;
          item.isExpanded = !collapsedIds.has(itemId);
        }

        children.push(item);
      });

      if (children.length === 0) {
        return;
      }

      // A pinned group never opens in place; the renderer shows its pages beside the header.
      footer.push({ ...toModelItem(header), children });
    });

    return footer;
  }, [appContext, collapsedIds, navItems]);

  // The entry each nested entry is drawn under: a task view under Tasks, a nested entry under its
  // parent, a page of a pinned group under its header or under the parent nested in that group.
  const parentById = useMemo(() => {
    const map = new Map<string, INavModelItem>();
    bodyGroups.forEach((group) => {
      group.items.forEach((item) => {
        item.children?.forEach((child) => map.set(child.id, item));
      });
    });
    footerItems.forEach((header) => {
      header.children?.forEach((child) => {
        map.set(child.id, header);
        child.children?.forEach((grandchild) => map.set(grandchild.id, child));
      });
    });

    return map;
  }, [bodyGroups, footerItems]);

  // The pinned header of every page of a pinned group, nested or not.
  const footerHeaderIdById = useMemo(() => {
    const map = new Map<string, string>();
    footerItems.forEach((header) => {
      header.children?.forEach((child) => {
        map.set(child.id, header.id);
        child.children?.forEach((grandchild) => map.set(grandchild.id, header.id));
      });
    });

    return map;
  }, [footerItems]);

  // The nearest entry the user can see: an entry under a closed parent highlights that parent, and a
  // page of a pinned group always highlights its header, because those pages are only ever in the
  // callout.
  const selectedId = useMemo(() => {
    const footerHeaderId = footerHeaderIdById.get(matchedId);
    if (footerHeaderId) {
      return footerHeaderId;
    }

    const parent = parentById.get(matchedId);
    if (parent && parent.isExpanded !== true) {
      return parent.id;
    }

    return matchedId;
  }, [footerHeaderIdById, matchedId, parentById]);

  // The layout entry whose view the user is on, if any. Its id alone goes into the effect below,
  // so a change of the open state, which rebuilds `parentById`, does not run the effect again.
  const visitedParentId = useMemo(() => {
    const parent = parentById.get(matchedId);

    return parent && layoutParentIds.has(parent.id) ? parent.id : undefined;
  }, [matchedId, parentById]);

  // Kept for the session, so a click on the entry returns to this view.
  useLayoutEffect(() => {
    if (visitedParentId) {
      setVisitedChild(visitedParentId, matchedId);
    }
  }, [matchedId, visitedParentId]);

  const selectItem = useCallback(
    (id: string) => {
      const item = selectableItems.find((candidate) => candidate.id === id);
      // A parent from the layout returns to the view visited last, if the user may still see it.
      const visitedChildId = layoutParentIds.has(id) ? getVisitedChild(id) : undefined;
      const visitedChild = visitedChildId
        ? selectableItems.find((candidate) => candidate.id === visitedChildId)
        : undefined;
      const target = visitedChild ?? item;
      if (!target?.route) {
        return;
      }

      history.push(target.route);
    },
    [history, selectableItems],
  );

  const toggleExpanded = useCallback(
    (id: string) => {
      toggleExpandedInStore(appContext, id);
    },
    [appContext],
  );

  const toggleGroup = useCallback(
    (id: NavGroupCaptionId) => {
      toggleExpandedInStore(appContext, navGroupStoreId(id));
    },
    [appContext],
  );

  return { bodyGroups, footerItems, matchedId, selectedId, selectItem, toggleExpanded, toggleGroup };
};
